import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  ShieldCheck,
  ShieldAlert,
  Search,
  Download,
  Loader2,
  Copy,
  CheckCircle2,
  Hash,
  Smartphone,
  MessageCircle,
  Mail,
  MapPin,
  Calendar,
  User,
  Building2,
  KeyRound,
  ExternalLink,
} from "lucide-react";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Field = ({ icon: Icon, label, value, mono = false, copyable = false }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      {Icon ? (
        <div className="mt-0.5 w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-slate-600" />
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </p>
        <p
          className={`mt-0.5 text-slate-900 break-all ${
            mono ? "font-mono text-xs" : "text-sm font-medium"
          }`}
        >
          {value || "—"}
        </p>
      </div>
      {copyable && value ? (
        <button
          onClick={onCopy}
          className="mt-1 p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 flex-shrink-0"
          title="Copiar"
        >
          {copied ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      ) : null}
    </div>
  );
};

const PublicVacationValidation = () => {
  const { signatureId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState(signatureId || "");

  const fetchValidation = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const res = await axios.get(
        `${API}/public/validate-vacation/${encodeURIComponent(sid)}`,
        { timeout: 20000 },
      );
      setData(res.data);
    } catch (e) {
      const msg =
        e?.response?.status === 404
          ? "Assinatura não localizada. Verifique se o ID está correto."
          : "Não foi possível validar agora. Tente novamente em instantes.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (signatureId) fetchValidation(signatureId);
  }, [signatureId, fetchValidation]);

  const onSearch = (e) => {
    e?.preventDefault();
    const sid = (searchInput || "").trim().toUpperCase();
    if (!sid) return;
    navigate(`/validar/${encodeURIComponent(sid)}`);
  };

  const downloadPdf = () => {
    const sid = data?.signature_id;
    if (!sid) return;
    window.open(
      `${API}/public/validate-vacation/${encodeURIComponent(sid)}/pdf`,
      "_blank",
    );
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/40"
      data-testid="public-validate-page"
    >
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-600 to-sky-700 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-none">
                AVI Segurança
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Portal público de validação
              </p>
            </div>
          </div>
          <a
            href="/"
            className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            Acessar app
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-5">
            <h1 className="text-lg font-bold text-slate-900 mb-1">
              Validação pública de Aviso de Férias
            </h1>
            <p className="text-sm text-slate-600 mb-4">
              Informe o <strong>ID único</strong> impresso no rodapé do
              documento (ex.: <code className="text-xs">AVI-FERIAS-2026-05-000001</code>)
              para verificar a autenticidade da assinatura digital.
            </p>
            <form onSubmit={onSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="AVI-FERIAS-AAAA-MM-NNNNNN"
                  className="pl-9 font-mono uppercase"
                  data-testid="validate-input"
                />
              </div>
              <Button type="submit" data-testid="validate-search-btn">
                Validar
              </Button>
            </form>
          </CardContent>
        </Card>

        {loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto mb-3" />
              <p className="text-sm text-slate-600">Validando assinatura…</p>
            </CardContent>
          </Card>
        )}

        {error && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-red-200 bg-red-50/40">
              <CardContent className="p-6 text-center">
                <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-red-800">
                  Não foi possível validar
                </p>
                <p className="text-xs text-red-700 mt-1">{error}</p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {data && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            data-testid="validate-result"
          >
            {/* Status banner */}
            <Card className="border-emerald-200 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4 text-white flex items-center gap-3">
                <ShieldCheck className="w-8 h-8 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold leading-tight">
                    ✅ Assinatura válida
                  </p>
                  <p className="text-xs text-emerald-50/90 mt-0.5">
                    {data.section_label
                      ? `Integridade confirmada — ${data.section_label}.`
                      : "Integridade confirmada — documento autêntico."}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={downloadPdf}
                  className="bg-white text-emerald-700 hover:bg-emerald-50"
                  data-testid="validate-download-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Ver PDF
                </Button>
              </div>
              <CardContent className="p-5">
                {data.section_label ? (
                  <div
                    className={`mb-3 -mx-1 rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wider border ${
                      data.part === "aviso"
                        ? "bg-sky-50 border-sky-200 text-sky-800"
                        : "bg-emerald-50 border-emerald-200 text-emerald-800"
                    }`}
                  >
                    Documento: {data.section_label}
                  </div>
                ) : null}
                <Field
                  icon={KeyRound}
                  label="ID único"
                  value={data.signature_id}
                  mono
                  copyable
                />
                <Field
                  icon={Calendar}
                  label="Assinado em"
                  value={formatDateTime(data.signed_at)}
                />
                <Field
                  icon={ShieldCheck}
                  label="Método"
                  value={data.method}
                />
              </CardContent>
            </Card>

            {/* Signer */}
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">
                  Assinante
                </p>
                <Field
                  icon={User}
                  label="Funcionário"
                  value={data.signer?.nome_mascarado}
                />
                <Field
                  icon={null}
                  label="Matrícula"
                  value={data.signer?.matricula}
                  mono
                />
                <Field
                  icon={null}
                  label="CPF"
                  value={data.signer?.cpf_mascarado}
                  mono
                />
                {data.signer?.posto_servico ? (
                  <Field
                    icon={Building2}
                    label="Posto de serviço"
                    value={data.signer.posto_servico}
                  />
                ) : null}
              </CardContent>
            </Card>

            {/* Cryptographic */}
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">
                  Criptografia & ICP-Brasil
                </p>
                <Field
                  icon={Building2}
                  label="Certificado"
                  value={data.icp_brasil?.subject_cn}
                />
                <Field
                  icon={null}
                  label="Tipo"
                  value={`${data.icp_brasil?.issuer} • ${data.icp_brasil?.type}`}
                />
                {data.hashes?.recibo_sha256 ? (
                  <Field
                    icon={Hash}
                    label={`SHA-256 (Recibo)${
                      data.part === "recibo" ? " — esta validação" : ""
                    }`}
                    value={data.hashes.recibo_sha256}
                    mono
                    copyable
                  />
                ) : null}
                {data.hashes?.aviso_sha256 ? (
                  <Field
                    icon={Hash}
                    label={`SHA-256 (Aviso)${
                      data.part === "aviso" ? " — esta validação" : ""
                    }`}
                    value={data.hashes.aviso_sha256}
                    mono
                    copyable
                  />
                ) : null}
              </CardContent>
            </Card>

            {/* Origin */}
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">
                  Origem da assinatura
                </p>
                {(() => {
                  const ch = (data.channel_used || "sms").toLowerCase();
                  const ident =
                    data.identifier_used_masked ||
                    data.phone_used_masked ||
                    "—";
                  if (ch === "whatsapp") {
                    return (
                      <Field
                        icon={MessageCircle}
                        label="WhatsApp validado (OTP)"
                        value={ident}
                        mono
                      />
                    );
                  }
                  if (ch === "email") {
                    return (
                      <Field
                        icon={Mail}
                        label="E-mail validado (OTP)"
                        value={ident}
                      />
                    );
                  }
                  return (
                    <Field
                      icon={Smartphone}
                      label="Telefone validado (OTP)"
                      value={ident}
                      mono
                    />
                  );
                })()}
                <Field
                  icon={MapPin}
                  label="IP do dispositivo"
                  value={data.origin?.ip_masked || "—"}
                  mono
                />
                <Field
                  icon={null}
                  label="Dispositivo"
                  value={
                    [data.origin?.browser, data.origin?.os, data.origin?.device]
                      .filter(Boolean)
                      .join(" • ") || "—"
                  }
                />
              </CardContent>
            </Card>

            <p className="text-center text-[11px] text-slate-500">
              Validado em tempo real pelo sistema AVI Segurança •{" "}
              {new Date().toLocaleString("pt-BR")}
            </p>
          </motion.div>
        )}

        {!data && !loading && !error && !signatureId ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600">
                Digite acima o ID único impresso no PDF do aviso de férias.
              </p>
            </CardContent>
          </Card>
        ) : null}
      </main>

      <footer className="max-w-3xl mx-auto px-4 py-6 text-center text-[11px] text-slate-500">
        © {new Date().getFullYear()} AVI Serviços de Segurança LTDA. Os
        documentos são assinados digitalmente com certificado ICP-Brasil
        e validação OTP em duas etapas.
      </footer>
    </div>
  );
};

export default PublicVacationValidation;
